import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { UsuarioDTO } from './autenticacion.models';
import { TokenService } from './token.service';
import { ListaDTO, Parametros } from 'src/app/compartido/utilidades/compartido.models';
import { AutorizacionService } from 'src/app/compartido/autorizacion/autorizacion.service';
import { Router } from '@angular/router';
import { OrganizacionService } from '../servicios/organizacion.service';
import { NotificacionesService } from '../servicios/notificaciones.service';
import { ErrorFrontend } from 'src/app/compartido/utilidades/error.utils';
import { EmpresaService } from '../servicios/empresa.service';

@Injectable({
  providedIn: 'root',
  deps: [
    HttpClient,
    TokenService,
    AutorizacionService,
    OrganizacionService,
    NotificacionesService,
    Router,
  ],
})
export class AutenticacionService {
  /** Usuario */
  private _usuario: UsuarioDTO | undefined;
  private _usuarioPromise$: Promise<UsuarioDTO | undefined> | undefined;
  /* Marca de vigencia de la caché: `limpiar()` la avanza para que la respuesta de una
     petición en vuelo no reviva el perfil de una sesión ya cerrada */
  private _vigencia: number = 0;
  public get usuario$(): Promise<UsuarioDTO | undefined> {
    if (this._usuarioPromise$) return this._usuarioPromise$;
    if (this._usuario) return Promise.resolve(this._usuario);
    const vigencia = this._vigencia;
    this._usuarioPromise$ = this._obtenerUsuario$().then((usuario: UsuarioDTO | undefined) => {
      if (vigencia !== this._vigencia) return usuario;
      this._usuario = usuario;
      this._usuarioPromise$ = undefined;
      return usuario;
    });
    return this._usuarioPromise$;
  }

  /** Autenticado */
  public get autenticado(): boolean {
    return !!this._tokenService.accessToken;
  }

  constructor(
    private readonly _router: Router,
    private readonly _http: HttpClient,
    private readonly _tokenService: TokenService,
    private readonly _empresaService: EmpresaService,
    private readonly _autorizacionService: AutorizacionService,
    private readonly _organizacionService: OrganizacionService,
    private readonly _notificacionesService: NotificacionesService,
  ) {}

  /**
   * Obtiene el usuario autenticado del servidor.
   * @returns {Promise<UsuarioDTO | undefined>} - El usuario autenticado, si existe (promise).
   * @author Juan Corral
   */
  private async _obtenerUsuario$(): Promise<UsuarioDTO | undefined> {
    return await firstValueFrom(this._http.get<UsuarioDTO>('autenticacion/usuarios/perfil/')).catch(
      () => undefined,
    );
  }

  /**
   * Obtiene los usuarios del servidor.
   * @param {Parametros} filtros - Filtros para la búsqueda de usuarios.
   * @returns {ListaDTO<UsuarioDTO>} - Lista de usuarios (promise).
   * @throws {ErrorFrontend} - Si ocurre un error al obtener los usuarios.
   * @author Juan Corral
   */
  public async obtenerUsuarios$(filtros: Parametros): Promise<ListaDTO<UsuarioDTO>> {
    return await firstValueFrom(
      this._http.get<ListaDTO<UsuarioDTO>>('autenticacion/usuarios/', {
        params: filtros,
      }),
    ).catch((error: HttpErrorResponse) => {
      throw new ErrorFrontend(error);
    });
  }

  /**
   * Cierra sesión del usuario en el servidor.
   * @author Juan Corral
   */
  public async logout$(): Promise<void> {
    await firstValueFrom(
      this._http.post('autenticacion/logout/', {
        token: this._tokenService.refreshToken,
      }),
    ).catch(() => undefined);
    this.limpiar();
    this._tokenService.limpiarToken();
    this._autorizacionService.limpiar();
    this._organizacionService.limpiar();
    this._notificacionesService.limpiar();
    this._empresaService.limpiar();
    this._router.navigate(['ingreso', 'login']);
  }

  /**
   * Limpia los datos almacenados en el servicio.
   * @autor Juan Corral
   */
  public limpiar(): void {
    this._usuario = undefined;
    this._usuarioPromise$ = undefined;
    this._vigencia++;
  }
}
